import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal, TextInput, Select, Button, Stack, Alert } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { APP_CONSTRAINTS } from '@/lib/constants/app';
import { usePlayers } from '@/features/players/usePlayers';
import type { ListlistDto } from '@lib2/model/listlist/ListlistDto';

const createAppSchema = z.object({
  name: z
    .string()
    .min(APP_CONSTRAINTS.NAME_MIN, 'Name is required')
    .max(APP_CONSTRAINTS.NAME_MAX)
    .trim(),
  rootLlId: z.string().min(1, 'Content Group is required'),
  defaultPlayerId: z.string().min(1, 'Player is required'),
});

type CreateAppFormData = z.infer<typeof createAppSchema>;

interface CreateAppDialogProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (name: string, rootLlId: number, defaultPlayerId: number) => void;
  isPending: boolean;
  contentGroups: ListlistDto[];
}

export function CreateAppDialog({
  opened,
  onClose,
  onSubmit,
  isPending,
  contentGroups,
}: CreateAppDialogProps) {
  const { playerOptions, defaultPlayerId } = usePlayers();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateAppFormData>({
    resolver: zodResolver(createAppSchema),
    defaultValues: {
      name: '',
      rootLlId: contentGroups.length === 1 ? String(contentGroups[0].llr.llId) : '',
      defaultPlayerId: defaultPlayerId ? String(defaultPlayerId) : '',
    },
  });

  const contentGroupOptions = contentGroups.map((cg) => ({
    value: String(cg.llr.llId),
    label: cg.llr.title,
  }));

  const hasMissingDeps = contentGroups.length === 0 || playerOptions.length === 0;

  const handleFormSubmit = (data: CreateAppFormData) => {
    onSubmit(data.name, parseInt(data.rootLlId, 10), parseInt(data.defaultPlayerId, 10));
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal opened={opened} onClose={handleClose} title="Create App" size="md">
      {hasMissingDeps ? (
        <Alert icon={<IconAlertCircle size={16} />} color="orange" mb="md">
          {contentGroups.length === 0 && playerOptions.length === 0
            ? 'You need at least one Content Group and one Player to create an app.'
            : contentGroups.length === 0
              ? 'You need at least one Content Group to create an app. Go to Apps > Content to create one.'
              : 'You need at least one Player to create an app.'}
        </Alert>
      ) : (
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <Stack gap="md">
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextInput
                  label="App Name"
                  placeholder="My App"
                  required
                  maxLength={APP_CONSTRAINTS.NAME_MAX}
                  error={errors.name?.message}
                  {...field}
                />
              )}
            />

            <Controller
              name="rootLlId"
              control={control}
              render={({ field }) => (
                <Select
                  label="Content Group"
                  placeholder="Select a content group"
                  required
                  data={contentGroupOptions}
                  error={errors.rootLlId?.message}
                  value={field.value}
                  onChange={(val) => field.onChange(val ?? '')}
                />
              )}
            />

            <Controller
              name="defaultPlayerId"
              control={control}
              render={({ field }) => (
                <Select
                  label="Default Player"
                  placeholder="Select a player"
                  required
                  data={playerOptions}
                  error={errors.defaultPlayerId?.message}
                  value={field.value}
                  onChange={(val) => field.onChange(val ?? '')}
                />
              )}
            />

            <Button type="submit" loading={isPending} fullWidth>
              Create App
            </Button>
          </Stack>
        </form>
      )}
    </Modal>
  );
}
